import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BroadcasterService } from '@app/modules/third-party/ng-broadcaster/src';
import { environment } from '@env';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslationService } from '@translation';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { getAssociatedOrganizationsMap$ } from '../../store/selectors/locations.selector';
import { getsupportedRegions$ } from '../../store/selectors/shared.selector';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';

import { RegionsInterface } from '../../interface/regions.interface';
import { Organization } from '../../models/organization.class';
import { User } from '../../models/user.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { UtilsService } from '../../services/utils/utils.service';
import {
    GetLocation,
    GetSupportedRegions,
    OpenUpdatePassword,
    SessionLogout,
    SetAssociatedLocations,
    SetDefaultTenant,
} from '../../store/actions';
import { getAssociatedOrganizations$, getSessionState$ } from '../../store/selectors';
import { ChangePasswordComponent } from '../changepassword/changepassword.component';
import { DigitalSignatureComponent } from '../digital-signature/digital-signature.component';
import { EditprofileComponent } from '../editprofile/editprofile.component';
import { UserPreferencesDialogComponent } from '../user-preferences-dialog/user-preferences-dialog.component';

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss'],
    standalone: false,
})
export class UserDetailsComponent implements OnInit {
    @Input() isNewUI = false;

    private unSubscribe = new Subject<void>();
    public loggedInUser: User = this._commonUtils.getFromStorage('user');
    public loggedInOrgName: any;
    public loggedInOrgId: any;
    public loggedInOrgLoginId: any;
    public lastLogin: any;
    public currentLanguage: string;
    public availableLanguages: any = [];
    public selectedLanguage: {
        label: string;
        id: string;
    };
    public region: string;
    public regionUrl: string;
    public availableRegions: RegionsInterface[] = [];
    public selectedRegion: {
        code: string;
        defaultLanguage: string;
        defaultTimeZone: string;
        name: string;
        regionUrl: string;
    };
    private associatedOrganizations: Organization[];
    private organizationsMap: { [property: string]: Organization } = {};
    public defaultTenantId: string | null = null;
    public defaultOrganization: Organization | null = null;

    constructor(
        public _commonUtils: CommonUtilsService,
        private _broadcaster: BroadcasterService,
        public dialog: MatDialog,
        public _utils: UtilsService,
        private store$: Store,
        private actions$: Actions,
        private _translate: TranslationService,
        private userPreferencesService: UserPreferencesService
    ) {}

    initiateSubscriptions = () => {
        this.store$
            .select(getAssociatedOrganizations$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(orgs => {
                this.associatedOrganizations = orgs;
            });
        this.store$
            .select(getAssociatedOrganizationsMap$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(map => {
                this.organizationsMap = map;
                const loggedInOrganization = this.organizationsMap?.[this.loggedInOrgId];
                this.loggedInOrgName = loggedInOrganization?.name;
                this.loggedInOrgLoginId = loggedInOrganization?.orgLoginId;
                this.updateDefaultOrganization();
            });
        this.store$
            .select(getsupportedRegions$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((data: any) => {
                this.availableRegions = data || [];
                this.selectedRegion = this.availableRegions.find(region => region.code === this.region);
                if (!this.selectedRegion && this.availableRegions.length === 1) {
                    this.selectedRegion = this.availableRegions[0];
                }
            });

        this.actions$.pipe(ofType(OpenUpdatePassword), takeUntil(this.unSubscribe)).subscribe(() => {
            this.dialog.open(ChangePasswordComponent, {
                panelClass: ['changePasswordModal', 'matDialogContainer', 'w-50'],
                disableClose: true,
            });
        });

        this.actions$.pipe(ofType(SetDefaultTenant), takeUntil(this.unSubscribe)).subscribe(() => {
            this.loadDefaultTenant();
        });
    };

    setUserDetails = () => {
        this.loggedInUser = this._commonUtils.getFromStorage('user');
        const loggedInOrganizationId = this._commonUtils.getFromStorage('loggedinorganizationid');
        const lastLoggedInOrgTime = this._commonUtils.getFromStorage('lastlogintime');
        this.loggedInOrgId = loggedInOrganizationId;
        const loggedInOrganization = this.organizationsMap?.[this.loggedInOrgId];
        this.loggedInOrgName = loggedInOrganization?.name;
        this.loggedInOrgLoginId = loggedInOrganization?.orgLoginId;
        this.store$.dispatch(GetLocation({ organizationId: loggedInOrganizationId, update: false, fromComponent: 'user-details' }));
        this.lastLogin = lastLoggedInOrgTime;
    };

    updateUserDetails = user => {
        this.loggedInUser = user;
        this._broadcaster.broadcast('editedUser', user);
    };

    openEditProfileModal = () => {
        const dialogRef = this.dialog.open(EditprofileComponent, {
            panelClass: ['editPopupWidth'],
            disableClose: true,
        });
        dialogRef.componentInstance.updateUserObject = this.updateUserDetails;
    };

    openDigitalSignatureModal = () => {
        const dialogRef = this.dialog.open(DigitalSignatureComponent, {
            panelClass: ['matDialogContainer', 'editPopupWidth'],
            disableClose: true,
        });
        dialogRef.componentInstance.updateUserObject = this.updateUserDetails;
    };

    switchToLoggedInOrganization = () => {
        const orgId = this._commonUtils.getFromStorage('loggedinorganizationId');
        if (this.isNewUI && this.associatedOrganizations?.length === 1 && this.associatedOrganizations[0]?.childNodes?.length === 0) {
            this.store$.dispatch(
                SetAssociatedLocations({
                    organizations: [],
                })
            );
        }
        if (orgId) {
            this._utils.navigateToOrganization(orgId);
        }
    };

    startLanguages = () => {
        this.availableLanguages = this._translate.getAvailableLangs();
        this.currentLanguage = this._translate.getActiveLang();
        this.selectedLanguage = this.availableLanguages.find(language => language.id === this.currentLanguage);
        this.region = environment.region;
    };

    setLanguage = (id: string) => {
        this._utils.setLanguage(id);
        this.currentLanguage = this._translate.getActiveLang();
        location.reload();
    };

    private getSupportedRegions = () => {
        this.store$.dispatch(GetSupportedRegions());
    };

    public setRegion = (code: string) => {
        const region = this.availableRegions.find(item => item.code === code);
        // region.defaultLanguage from the API is a display name (e.g. "Arabic"), not an ISO code.
        // Resolve it to the corresponding language ID (e.g. "ar") used by the i18n loader,
        // so the correct file (ar.json) is requested instead of causing a 404 for "Arabic.json".
        const matchedLang = (this.availableLanguages as { id: string; label: string }[]).find(
            lang => lang.label?.toLowerCase() === region.defaultLanguage?.toLowerCase()
        );
        const languageId = matchedLang?.id || 'en';
        this._utils.setLanguage(languageId);
        const url = new URL(
            region.regionUrl?.charAt(region.regionUrl.length - 1) === '/' ? region.regionUrl.slice(0, -1) : region.regionUrl
        );
        url.searchParams.set('skin', this.isNewUI ? 'new' : 'old');
        this._commonUtils.redirectToPage(url.toString(), true);
    };

    openUserPreferencesModal = () => {
        /* mirrors the create/update orchestration slide panel: 60% wide capped at 1000px, full height, docked right */
        this.dialog.open(UserPreferencesDialogComponent, {
            panelClass: ['userPreferencesModal'],
            width: '94vw',
            maxWidth: '1560px',
            height: '85vh',
            maxHeight: '820px',
            disableClose: true,
            autoFocus: false,
        });
    };

    openChangePasswordModal = () => {
        this.store$.dispatch(OpenUpdatePassword());
        this.store$.dispatch(OpenUpdatePassword());
    };

    onLogout = () => {
        this.store$.dispatch(SessionLogout());
        this._commonUtils.deleteCookie('domain');
    };

    private loadDefaultTenant(): void {
        this.defaultTenantId = this.userPreferencesService.getPreference<string>('defaultTenantId', null);
        this.updateDefaultOrganization();
    }

    private updateDefaultOrganization(): void {
        if (this.defaultTenantId && this.organizationsMap) {
            this.defaultOrganization = this.organizationsMap?.[this.defaultTenantId] || null;
            !this.defaultOrganization?.id && this.store$.dispatch(GetLocation({ organizationId: this.defaultTenantId, update: true }));
        } else {
            this.defaultOrganization = null;
        }
    }

    ngOnInit(): void {
        this.store$
            .select(getSessionState$)
            .pipe(
                filter(session => !!session),
                take(1)
            )
            .subscribe(() => {
                this.getSupportedRegions();
                this.initiateSubscriptions();
                this.setUserDetails();
                this.startLanguages();
                this.loadDefaultTenant();
            });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
